import { useMemo } from "react"

import { MAX_CACHE_AGE } from "../../../lib/cacheConfig"
import type { IWidgetResult, IWidgetState } from "../homeDashboard.types"

/**
 * Home widgets poll quietly and catch up when the tab comes back, like brain.aixlabs.co. Each widget is its own
 * request (skipBatch): batched heavy reads stall on the Worker shared Postgres socket, and one slow widget must not
 * hold the rest. cacheTime matches the persisted cache, so widgets stay in localStorage while you are on other pages
 * and the home page opens on the last numbers, then refreshes.
 */
export const WIDGET_QUERY_OPTIONS = {
  refetchInterval: 60_000,
  refetchOnWindowFocus: true,
  // Reads time out after 30s (lib/trpc.ts); one retry, not react-query's three, so a hang surfaces in ~1 min
  retry: 1,
  cacheTime: MAX_CACHE_AGE,
  trpc: { context: { skipBatch: true } },
} as const

/**
 * For data that syncs daily (revenue, ad spend, coverage, GitHub delivery): poll every 10 min and skip refetching on
 * tab focus while under 5 min old. The API caches these for 10 min too, so faster polling only re-reads Redis.
 */
export const WIDGET_DAILY_QUERY_OPTIONS = {
  ...WIDGET_QUERY_OPTIONS,
  refetchInterval: 10 * 60_000,
  staleTime: 5 * 60_000,
} as const

interface IQueryLike<T> {
  data: T | undefined
  error: { message: string } | null
  isLoading: boolean
  isFetching: boolean
  dataUpdatedAt: number
  refetch: () => unknown
}

/** Adapts a react-query result: cached data renders immediately while a refetch runs. */
export const useQueryWidget = <T>(query: IQueryLike<T>, render: (data: T) => IWidgetResult): IWidgetState => {
  const { data, error, isLoading, isFetching, dataUpdatedAt, refetch } = query
  const result = useMemo(() => (data === undefined ? undefined : render(data)), [data, render])
  return {
    result,
    error: error?.message,
    isLoading: isLoading && data === undefined,
    isFetching,
    updatedAt: dataUpdatedAt || undefined,
    refetch: () => void refetch(),
  }
}
